import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import CookieBanner from './components/CookieBanner';
import './styles/global.css';
import HomePage        from './pages/public/HomePage';
import FindTutors      from './pages/public/FindTutors';
import TutorProfile    from './pages/public/TutorProfile';
import HowItWorks      from './pages/public/HowItWorks';
import BecomeTutor     from './pages/public/BecomeTutor';
import Terms           from './pages/public/Terms';
import Privacy         from './pages/public/Privacy';
import Login           from './pages/auth/Login';
import RegisterParent  from './pages/auth/RegisterParent';
import RegisterTutor   from './pages/auth/RegisterTutor';
import ForgotPassword  from './pages/auth/ForgotPassword';
import ResetPassword   from './pages/auth/ResetPassword';
import TutorOnboarding  from './pages/tutor/Onboarding';
import TutorDashboard   from './pages/tutor/Dashboard';
import TutorProfileEdit from './pages/tutor/ProfileEdit';
import TutorAvailability from './pages/tutor/Availability';
import TutorBookings    from './pages/tutor/Bookings';
import TutorMessages    from './pages/tutor/Messages';
import TutorPayouts     from './pages/tutor/Payouts';
import TutorSettings    from './pages/tutor/Settings';
import ParentDashboard from './pages/parent/Dashboard';
import ParentWallet    from './pages/parent/Wallet';
import ParentBookings  from './pages/parent/Bookings';
import ParentMessages  from './pages/parent/Messages';
import ParentSettings  from './pages/parent/Settings';
import AdminDashboard   from './pages/admin/Dashboard';
import AdminTutorQueue  from './pages/admin/TutorQueue';
import AdminTutorDetail from './pages/admin/TutorDetail';
import AdminParents     from './pages/admin/Parents';
import AdminBookings    from './pages/admin/Bookings';
import AdminDisputes    from './pages/admin/Disputes';
import AdminPayouts     from './pages/admin/Payouts';
import AdminReports     from './pages/admin/Reports';
import AdminConcerns    from './pages/admin/Concerns';
import { AdminApprovedTutors } from './pages/admin/AdminPages';
const RequireAuth = ({ children, role }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner spinner-lg"/></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
};
const RedirectIfAuth = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner spinner-lg"/></div>;
  if (user) {
    if (user.role === 'admin')  return <Navigate to="/admin/dashboard" replace />;
    if (user.role === 'tutor')  return <Navigate to="/tutor/dashboard" replace />;
    if (user.role === 'parent') return <Navigate to="/parent/dashboard" replace />;
  }
  return children;
};
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/"               element={<HomePage />} />
          <Route path="/find-tutors"    element={<FindTutors />} />
          <Route path="/tutor/:slug"    element={<TutorProfile />} />
          <Route path="/how-it-works"   element={<HowItWorks />} />
          <Route path="/become-a-tutor" element={<BecomeTutor />} />
          <Route path="/terms"          element={<Terms />} />
          <Route path="/privacy"        element={<Privacy />} />
          <Route path="/login"                  element={<RedirectIfAuth><Login /></RedirectIfAuth>} />
          <Route path="/register/parent"        element={<RedirectIfAuth><RegisterParent /></RedirectIfAuth>} />
          <Route path="/register/tutor"         element={<RedirectIfAuth><RegisterTutor /></RedirectIfAuth>} />
          <Route path="/forgot-password"        element={<ForgotPassword />} />
          <Route path="/reset-password/:token"  element={<ResetPassword />} />
          <Route path="/tutor/onboarding"    element={<RequireAuth role="tutor"><TutorOnboarding /></RequireAuth>} />
          <Route path="/tutor/dashboard"     element={<RequireAuth role="tutor"><TutorDashboard /></RequireAuth>} />
          <Route path="/tutor/profile"       element={<RequireAuth role="tutor"><TutorProfileEdit /></RequireAuth>} />
          <Route path="/tutor/availability"  element={<RequireAuth role="tutor"><TutorAvailability /></RequireAuth>} />
          <Route path="/tutor/bookings"      element={<RequireAuth role="tutor"><TutorBookings /></RequireAuth>} />
          <Route path="/tutor/messages"      element={<RequireAuth role="tutor"><TutorMessages /></RequireAuth>} />
          <Route path="/tutor/payouts"       element={<RequireAuth role="tutor"><TutorPayouts /></RequireAuth>} />
          <Route path="/tutor/settings"      element={<RequireAuth role="tutor"><TutorSettings /></RequireAuth>} />
          <Route path="/parent/dashboard"    element={<RequireAuth role="parent"><ParentDashboard /></RequireAuth>} />
          <Route path="/parent/wallet"       element={<RequireAuth role="parent"><ParentWallet /></RequireAuth>} />
          <Route path="/parent/bookings"     element={<RequireAuth role="parent"><ParentBookings /></RequireAuth>} />
          <Route path="/parent/messages"     element={<RequireAuth role="parent"><ParentMessages /></RequireAuth>} />
          <Route path="/parent/settings"     element={<RequireAuth role="parent"><ParentSettings /></RequireAuth>} />
          <Route path="/admin/dashboard"         element={<RequireAuth role="admin"><AdminDashboard /></RequireAuth>} />
          <Route path="/admin/tutors/pending"    element={<RequireAuth role="admin"><AdminTutorQueue /></RequireAuth>} />
          <Route path="/admin/tutors/approved"   element={<RequireAuth role="admin"><AdminApprovedTutors /></RequireAuth>} />
          <Route path="/admin/tutors/:id"        element={<RequireAuth role="admin"><AdminTutorDetail /></RequireAuth>} />
          <Route path="/admin/parents"           element={<RequireAuth role="admin"><AdminParents /></RequireAuth>} />
          <Route path="/admin/bookings"          element={<RequireAuth role="admin"><AdminBookings /></RequireAuth>} />
          <Route path="/admin/disputes"          element={<RequireAuth role="admin"><AdminDisputes /></RequireAuth>} />
          <Route path="/admin/payouts"           element={<RequireAuth role="admin"><AdminPayouts /></RequireAuth>} />
          <Route path="/admin/reports"           element={<RequireAuth role="admin"><AdminReports /></RequireAuth>} />
          <Route path="/admin/concerns"          element={<RequireAuth role="admin"><AdminConcerns /></RequireAuth>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <CookieBanner />
      </BrowserRouter>
    </AuthProvider>
  );
}
export default App;
